/**
 * Measured wait expectations — 90th-percentile (worst-case), NOT guesses.
 *
 * Source: standalone Playwright browser samples against the deployed raisy.team
 * stack (real BE, real network), 2026-09-02 — runs logged in /tmp/wizard_qa/measure*.mjs:
 *   wizard scan (flixn.ai)  n=4 → 12.0, 12.0, 14.0, 14.0 s
 *   wizard scan (bosch.com) n=2 → 26.0, 38.1 s   ← homepage-SIZE dependent
 *   rank candidates  PRE-N+1-fix n=5 → 3.0 ×4 + one cold 22.5 s  → p90 22.5 s
 *   rank candidates  POST-N+1-fix (bulk BD90.6 load, 2026-09-02) → cold 623 ms,
 *                     warm 518–639 ms  → budget 3 s
 *   assemble drafts         n=3 → ~1, ~2, ~1 s (1 s granularity)  → p90 ~2 s
 *
 * Budgets are p90 rounded UP so the promise ("worst case ~N s") is honest ≥90% of
 * the time. The wizard scan budget is set to the LARGE-homepage worst (bosch.com
 * 38 s → 45 s) because a single fixed number must not over-promise on heavy sites;
 * small-site scans (~12–15 s) still trigger the "faster than expected" celebration.
 * Re-measure after infra / funnel / scraper changes — do not tune by feel.
 */
export const WAIT_BUDGET_MS = {
  /** Entity-website scan → "Entities ready". Homepage-size dependent: flixn.ai ~12–15s, bosch.com 26–38s. */
  wizardScan: 45_000,
  /** "Find investors for this raise" → candidate table. Post-N+1-fix (2026-09-02): cold 623ms, warm ~0.5s. */
  rankCandidates: 3_000,
  /** "Assemble … email drafts — preview" → dry-run preview. Typical ~1–2s + Hunter email
      acquisition (D-064/MR21) on first run (~a few seconds for the selected set). p90 with
      acquisition ≈ 15s worst case; idempotent — later runs skip already-enriched funds. */
  assembleDrafts: 15_000,
} as const;

/**
 * Build the "faster than expected" celebration line when a wait finished well under
 * its promised worst-case budget. Returns null when the wait was NOT notably faster
 * than budget (no noise) or too fast to be a real wait (<500ms).
 *
 * Copy is measured against the SAME number quoted in the upfront microcopy, so the
 * product never over-promises and the celebration is always honest.
 */
export function fastLine(what: string, elapsedMs: number, budgetMs: number): string | null {
  if (elapsedMs < 500) return null; // too fast to be a real wait (or a stub)
  const ratio = elapsedMs / budgetMs;
  if (ratio >= 0.85) return null; // ≥85% of the promised worst case — not notably faster
  const secs = Math.round(elapsedMs / 1000);
  const pct = Math.round((1 - ratio) * 100);
  return `${what} in ${secs}s — ${pct}% faster than the ~${Math.round(budgetMs / 1000)}s worst case.`;
}
